import { useEffect, useState } from 'react'
import { NavLink, Outlet, useParams, useNavigate } from 'react-router'
import { clearToken } from '../lib/auth.ts'
import { api } from '../lib/api.ts'

interface SiteRow {
  id: string
  name: string
}

/**
 * Per-site shell: sidebar with the 5 v1.0 sections + outlet for the
 * active screen. Site picker is a real `<select>` populated from
 * /api/sites; changing it navigates to /sites/<newId>/ preserving the
 * current section (Cockpit, Articles, etc).
 */
export function SiteLayout() {
  const { siteId = 'chc', '*': rest = '' } = useParams()
  const navigate = useNavigate()
  const [sites, setSites] = useState<SiteRow[] | null>(null)

  useEffect(() => {
    let cancelled = false
    api<{ sites: SiteRow[] }>('/api/sites')
      .then((r) => !cancelled && setSites(r.sites))
      .catch(() => !cancelled && setSites([]))
    return () => { cancelled = true }
  }, [])

  async function logout() {
    try {
      await api('/api/auth/logout', { method: 'POST' })
    } finally {
      clearToken()
      navigate('/login', { replace: true })
    }
  }

  const navItems: { to: string; label: string; disabled?: boolean; hint?: string }[] = [
    { to: '', label: 'Cockpit' },
    { to: 'articles', label: 'Articles' },
    { to: 'universal', label: 'Universal' },
    { to: 'keywords', label: 'Keywords' },
    { to: 'train', label: 'Teach judge' },
    { to: 'corpus', label: 'Corpus' },
    { to: 'analytics', label: 'Analytics' },
    { to: 'standards', label: 'Standards' },
    { to: 'settings', label: 'Settings', disabled: true, hint: 'Coming soon' },
  ]

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="brand">ContentHub</div>
        {sites && sites.length > 1 ? (
          <select
            className="site-picker"
            value={siteId}
            onChange={(e) => {
              // Preserve the current section path (cockpit/articles/etc.)
              // when switching sites. Don't carry over per-article URLs —
              // bounce to that section's index.
              const sectionTail = window.location.pathname.split(`/sites/${siteId}/`)[1] ?? ''
              const section = sectionTail.split('/')[0] ?? ''
              navigate(`/sites/${e.target.value}/${section}`)
            }}
            style={{
              width: '100%',
              padding: '0.4rem 0.5rem',
              borderRadius: 'var(--radius)',
              border: '1px solid var(--border-strong)',
              background: 'var(--bg-elev)',
              fontFamily: 'inherit',
              fontSize: '0.85rem',
              marginBottom: '0.75rem',
              cursor: 'pointer',
            }}
            title="Switch site"
          >
            {sites.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        ) : (
          <div className="site-picker">{siteId.toUpperCase()}</div>
        )}
        {navItems.map((item) =>
          item.disabled ? (
            <div
              key={item.to}
              className="nav-item"
              style={{ color: 'var(--text-faint)', cursor: 'not-allowed' }}
              title={item.hint ?? 'Coming soon'}
            >
              <span>{item.label}</span>
              <span style={{ fontSize: '0.7rem' }}>·</span>
            </div>
          ) : (
            <NavLink
              key={item.to}
              to={`/sites/${siteId}/${item.to}`}
              className={({ isActive }) =>
                isActive ? 'nav-item active' : 'nav-item'
              }
            >
              {item.label}
            </NavLink>
          ),
        )}
        <button type="button" className="logout" onClick={logout}>
          Sign out
        </button>
      </aside>
      <main className="main">
        <Outlet />
      </main>
    </div>
  )
}
